'use client';

import type { ReactNode } from 'react';

type SettingsRowProps = {
  /** Left-column label. Omit for a control-only row. */
  label?: ReactNode;
  /** Left-column supporting copy under the label. */
  description?: ReactNode;
  /**
   * Puts an id on the description so a control can point `aria-describedby` at
   * it. Worth doing wherever the description states a RULE the field obeys —
   * a legitimate empty state, a unit, a range — rather than merely restating
   * the label, because a disabled or newly-focused control announces its
   * accessible name and nothing else.
   */
  descriptionId?: string;
  /**
   * Smaller tertiary copy under the description — the prototype's `.dl-hint`.
   * Use for provenance/metadata, not for anything the user has to read.
   */
  hint?: ReactNode;
  /** Right-column control(s) — Input, Switch, Button, … */
  children?: ReactNode;
  /**
   * Bottom hairline. The mockup divides consecutive rows and leaves the last
   * one open, so pass `divider={false}` on the final row of a group.
   */
  divider?: boolean;
  /**
   * Nested parameter panel below both columns — the prototype's `.set-params`,
   * revealed when a gate toggle is on. Fill it with `SettingsParamRow`s.
   */
  params?: ReactNode;
  /**
   * Full-width content below both columns — a validation message, or a details
   * disclosure. It carries its own top margin: butted straight against the
   * description it reads as a third line of copy rather than a separate control.
   */
  footer?: ReactNode;
  /**
   * Dims the label column, matching the prototype's `.setrow.off .lead` — used
   * when the row's gate is off so its parameters read as inactive.
   */
  dimmed?: boolean;
  /** Associates the label with its control; renders the label as a <label>. */
  htmlFor?: string;
  className?: string;
};

/**
 * The mockup's most repeated pattern (`.srow`): a 440px label/description
 * column, a 64px gutter, then the control column, closed by a hairline.
 *
 * MEASUREMENTS ARE THE PROTOTYPE'S OWN, read from its stylesheet rather than
 * from a screenshot:
 *
 *     .srow-content  padding: 16px 0                    -> py-space-16
 *     .srow-left     flex: 0 0 440px; padding-right:24px -> basis-[27.5rem] + pr-space-24
 *     .srow-value    margin-left: calc(504px - 440px)    -> md:gap-space-64
 *     .srow-label    14px / 20px / 500                   -> typography-label-md-strong
 *     .srow-desc     12px / 16px, tertiary, margin-top:4 -> typography-body-xs + gap-space-4
 *
 * Two deliberate deviations, both taking the design system's token over the
 * prototype's raw value: `typography-body-xs` resolves to a 15px line-height
 * against the prototype's 16px, and the 4px title-to-description gap comes from
 * this column's `gap-space-4` rather than a margin on the description — same
 * result, one mechanism.
 *
 * The DS ships no layout primitives, so this is flex over DS token utilities and
 * nothing else — no colours, sizes or spacing that aren't tokens (the two
 * `[27.5rem]`/`[15rem]` widths are the prototype's own lead-column measure,
 * which has no token equivalent).
 */
export const SettingsRow = ({
  label,
  description,
  descriptionId,
  hint,
  children,
  divider = true,
  params,
  footer,
  dimmed = false,
  htmlFor,
  className,
}: SettingsRowProps) => {
  const LabelTag = htmlFor ? 'label' : 'span';

  return (
    <div
      data-lula-ds=""
      // last:border-b-0 mirrors the prototype's `.setrow:last-child` — the final
      // row of a group closes the list rather than drawing a dangling hairline,
      // so callers never have to work out which row is last.
      className={`flex flex-col py-space-16 font-sans${
        divider ? ' border-b border-solid border-border-subtle last:border-b-0' : ''
      }${className ? ` ${className}` : ''}`}
    >
      <div className="flex flex-wrap items-start gap-space-16 md:gap-space-64">
        {(label || description || hint) && (
          <div
            // The space before the interpolation is load-bearing. Tailwind's
            // extractor reads this file as text, and a utility written flush
            // against `${` is not recognised as a candidate — so the class is
            // never generated and silently does nothing. `md:basis-[25rem]` sat
            // here unemitted for exactly that reason until it was measured
            // against the compiled output; every other utility in the kit that
            // abuts an interpolation survives only because it also appears
            // elsewhere with whitespace around it.
            className={`flex min-w-0 shrink grow-0 basis-full flex-col gap-space-4 pr-space-24 md:min-w-[15rem] md:max-w-[27.5rem] md:basis-[27.5rem] ${
              dimmed ? 'opacity-55' : ''
            }`}
          >
            {label && (
              <LabelTag
                htmlFor={htmlFor}
                className="typography-label-md-strong m-0 text-text-primary"
              >
                {label}
              </LabelTag>
            )}
            {description && (
              <span id={descriptionId} className="typography-body-xs m-0 text-text-tertiary">
                {description}
              </span>
            )}
            {hint && (
              <div className="typography-body-xs flex min-w-0 flex-col gap-space-2 text-text-tertiary">
                {hint}
              </div>
            )}
          </div>
        )}
        {children && (
          <div className="flex min-w-0 flex-1 flex-wrap items-center gap-space-8">{children}</div>
        )}
      </div>
      {params && (
        <div className="mt-space-12 border-t border-solid border-border-subtle pt-space-12">
          {params}
        </div>
      )}
      {footer && <div className="mt-space-12 min-w-0">{footer}</div>}
    </div>
  );
};

export default SettingsRow;
